import { api } from './config';
import type { MitreGroup, TTP, Software, YaraRule } from '../types/api';

export async function searchByIOC(query: string): Promise<MitreGroup[]> {
  try {
    // First get the group_id from IOC search
    const response = await api.get<{ group_id: string }[]>(`/api/search/ioc/${encodeURIComponent(query)}`);
    
    // Then fetch full group details for each group_id
    const groupDetails = await Promise.all(
      response.data.map(result => getGroupInfo(result.group_id))
    );

    return groupDetails;
  } catch (error) {
    if ((error as any).response?.status === 401 || (error as any).response?.status === 403) {
      throw new Error('Authentication failed');
    }
    console.error('Error searching IOC:', error);
    return [];
  }
}

export async function getGroupInfo(groupId: string): Promise<MitreGroup> {
  try {
    const response = await api.get<MitreGroup>(`/api/groups/${encodeURIComponent(groupId)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching group info:', error);
    throw error;
  }
}

export async function getGroupTTPs(groupId: string): Promise<TTP[]> {
  try {
    const response = await api.get<TTP[]>(`/api/groups/${encodeURIComponent(groupId)}/ttps`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching group TTPs:', error);
    return [];
  }
}

export async function getGroupSoftware(groupId: string): Promise<Software[]> {
  try {
    const response = await api.get<Software[]>(`/api/groups/${encodeURIComponent(groupId)}/software`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching group software:', error);
    return [];
  }
}

export async function getGroupYaraRules(groupId: string): Promise<YaraRule[]> {
  try {
    const response = await api.get<YaraRule[]>(`/api/groups/${encodeURIComponent(groupId)}/yara`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching group Yara rules:', error);
    return [];
  }
}

export async function searchGroups(query: string): Promise<MitreGroup[]> {
  try {
    // Search by group ID if query matches the pattern G#### or group ID pattern
    if (/^G\d{4}$/i.test(query)) {
      const group = await getGroupInfo(query.toUpperCase());
      return [group];
    }

    // Direct group search using the groups endpoint
    try {
      const response = await api.get<MitreGroup>(`/api/groups/${encodeURIComponent(query)}`);
      return [response.data];
    } catch (error) {
      if ((error as any).response?.status !== 404) {
        throw error;
      }
      return [];
    }
  } catch (error) {
    if ((error as any).response?.status === 401 || (error as any).response?.status === 403) {
      throw new Error('Authentication failed');
    }
    console.error('Error searching groups:', error);
    return [];
  }
}

// Helper function to detect if a string is a hash
function isHash(query: string): boolean {
  // Match common hash patterns (MD5, SHA1, SHA256)
  const hashPattern = /^[a-fA-F0-9]{32,64}$/;
  return hashPattern.test(query);
}

// Helper function to detect if a string is an IP address (IPv4 or IPv6)
function isIP(query: string): boolean {
  // IPv4
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  // IPv6 (simplified)
  const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){2,7}[0-9a-fA-F]{1,4}$/;
  return ipv4Pattern.test(query) || ipv6Pattern.test(query);
}

// Helper function to detect if a string is a domain
function isDomain(query: string): boolean {
  const domainPattern = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
  return domainPattern.test(query);
}

export async function searchByQuery(query: string): Promise<MitreGroup[]> {
  try {
    if (isHash(query) || isIP(query) || isDomain(query)) {
      // If query is a hash, IP, or domain, search by IOC
      return searchByIOC(query);
    } else {
      // Otherwise search by group name/alias/ID
      return searchGroups(query);
    }
  } catch (error) {
    console.error('Error searching:', error);
    return [];
  }
}